import React from 'react';
import { Link } from 'react-router-dom'; // Required if columns or actions involve React Router Links

/**
 * A reusable table component that dynamically renders columns and actions.
 *
 * @param {Object} props - The component props.
 * @param {Array<Object>} props.data - The array of data objects to display in the table rows.
 * @param {Array<Object>} props.columns - An array defining the table columns.
 *   Each column object can have:
 *   - `header`: The text for the column header.
 *   - `accessor`: (Optional) The key in the data object to access for this column's value.
 *   - `render`: (Optional) A function `(rowData) => JSX.Element` for custom cell rendering.
 *   - `headerClassName`: (Optional) CSS class for the `<th>` element.
 * @param {Array<Object>} props.actions - (Optional) An array defining action buttons for each row.
 *   Each action object can have:
 *   - `icon`: The JSX element for the icon (e.g., `<EyeIcon />`).
 *   - `tooltip`: The tooltip text for the button.
 *   - `onClick`: A function `(rowData) => void` to call when the button is clicked.
 *   - `className`: (Optional) Additional CSS classes for the button.
 * @param {string} props.keyAccessor - The property name in each data object to use as a unique key for rows. Defaults to 'id'.
 */
function DynamicTable({ data, columns, actions, keyAccessor = 'id' }) {
    if (!data || data.length === 0) {
        return <p className="text-muted text-center mt-4">No hay datos disponibles para mostrar.</p>;
    }

    return (
        <div className="table-responsive">
            <table className="table table-hover align-middle">
                <thead>
                    <tr>
                        {columns.map((col, index) => (
                            <th key={col.header || `col-${index}`} scope="col" className={col.headerClassName || 'text-start'}>
                                {col.header}
                            </th>
                        ))}
                        {actions && actions.length > 0 && (
                            <th scope="col" className="text-center">Acciones</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {data.map(row => (
                        <tr key={row[keyAccessor]}>
                            {columns.map((col, index) => (
                                <td key={`${row[keyAccessor]}-${col.accessor || `cell-${index}`}`} className="text-start">
                                    {col.render ? col.render(row) : row[col.accessor]}
                                </td>
                            ))}
                            {actions && actions.length > 0 && (
                                <td className="text-center">
                                    {actions.map((action, actionIndex) => (
                                        <button
                                            key={`${row[keyAccessor]}-action-${actionIndex}`}
                                            type="button"
                                            className={`btn btn-action-round-outline btn-sm ${action.className || ''} ${actionIndex < actions.length - 1 ? 'me-1' : ''}`}
                                            data-bs-toggle="tooltip"
                                            data-bs-placement="top"
                                            title={action.tooltip}
                                            onClick={() => action.onClick(row)}
                                        >
                                            {action.icon}
                                        </button>
                                    ))}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default DynamicTable;